'use client';

import loader from '@monaco-editor/loader';
import { useEffect, useState } from 'react';

import { ChevronBottomIcon } from '@/shared';
import { jetBrainsMono, sourceCodePro } from '@/shared/config';

import { INTERVIEW_THEME_NAME, interviewTheme, theme } from './config';
import { jsonMonarchLanguage } from './config/json-monarch';
import { getMonacoLanguage } from './utils/getMonacoLanguage';

export type CodeViewerProps = {
	code: string;
	lang: string;
	collapse?: boolean;
	/** Показывать колонку с номерами строк слева. */
	showLineNumbers?: boolean;
	/** Имя темы monaco. По умолчанию 'vs'. */
	theme?: string;
};

/** Сколько строк показываем в свёрнутом виде; совпадает с условием показа «Развернуть». */
const COLLAPSED_LINE_LIMIT = 7;

const sliceCode = (code: string, collapse: boolean): string => {
	if (!collapse) return code;
	return code.split('\n').slice(0, COLLAPSED_LINE_LIMIT).join('\n');
};

const hasHiddenTail = (code: string): boolean =>
	code.split('\n').length > COLLAPSED_LINE_LIMIT;

export function CodeViewer({
	code,
	lang,
	showLineNumbers,
	theme: themeName,
	...props
}: CodeViewerProps) {
	const [collapse, setCollapse] = useState(props.collapse ?? false);
	const [highlight, setHighlight] = useState<{
		code: string;
		lang: string;
		themeName: string | undefined;
		html: string;
	} | null>(null);
	const visibleCode = sliceCode(code, collapse);
	const onExpand = () => setCollapse(false);

	useEffect(() => {
		let active = true;
		const cancelable = loader.init();

		cancelable
			.then(async (monaco) => {
				if (!active) return;
				monaco.editor.defineTheme('vs', theme);
				monaco.editor.defineTheme(INTERVIEW_THEME_NAME, interviewTheme);
				// colorize() читает токены через активную тему — переключаем явно,
				// чтобы не подхватить тему, оставленную после ухода с другой страницы.
				monaco.editor.setTheme(themeName ?? 'vs');

				if (
					!monaco.languages.getLanguages().some((l) => l.id === 'json-monarch')
				) {
					monaco.languages.register({ id: 'json-monarch' });
					monaco.languages.setMonarchTokensProvider(
						'json-monarch',
						jsonMonarchLanguage,
					);
				}

				const monacoLanguage = getMonacoLanguage(lang);
				const colorizeLanguage =
					monacoLanguage === 'json' ? 'json-monarch' : monacoLanguage;
				const html = await monaco.editor.colorize(
					visibleCode,
					colorizeLanguage,
					{},
				);
				if (active) setHighlight({ code: visibleCode, lang, themeName, html });
			})
			.catch(() => null);

		return () => {
			active = false;
			cancelable.cancel();
		};
	}, [visibleCode, lang, themeName]);

	const codeContent =
		highlight?.code === visibleCode &&
		highlight.lang === lang &&
		highlight.themeName === themeName
			? { dangerouslySetInnerHTML: { __html: highlight.html } }
			: { children: visibleCode };
	const lineCount = visibleCode.split('\n').length;

	// Интервью-палитра: JetBrains Mono, 13px / line-height 1.7 (как в демо).
	const isInterview = themeName === INTERVIEW_THEME_NAME;
	const fontClassName = isInterview
		? jetBrainsMono.className
		: sourceCodePro.className;
	const sizeClassName = isInterview ? 'text-[13px] leading-[1.7]' : 'text-sm';

	return (
		<>
			{showLineNumbers ? (
				<div
					className={`${fontClassName} ${sizeClassName} flex w-full overflow-x-auto py-3 font-normal`}
				>
					<pre
						aria-hidden
						className="shrink-0 select-none pl-3 pr-3 text-right tabular-nums text-gray-4"
					>
						{Array.from({ length: lineCount }, (_, i) => i + 1).join('\n')}
					</pre>
					<pre className="min-w-0 flex-1 pr-2" {...codeContent} />
				</div>
			) : (
				<pre
					className={`${fontClassName} ${sizeClassName} w-full overflow-x-auto px-2 py-3 font-normal`}
					{...codeContent}
				/>
			)}
			{collapse && hasHiddenTail(code) && (
				<button
					onClick={onExpand}
					className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-b-lg border-t border-t-gray-4 bg-gray-5 px-2 py-1 text-center text-xs text-gray-3"
				>
					Развернуть <ChevronBottomIcon className="size-4" />
				</button>
			)}
		</>
	);
}
